// store/slices/contactSlice.ts

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllContactsApi,
  getContactsByClientApi,
  addContactApi,
  updateContactApi,
  deleteContactApi,
  addBulkContactsToGroupApi,
  getContactDetailsApi,
  getContactProfileApi,
  getContactDesignationsAndGroupsApi,
} from "../../api/contactActions";

// Types based on API response
export interface Contact {
  contact_id: number;
  contact_name: string;
  contact_email?: string;
  contact_mobile?: string;
  dob?: string;
  address?: string;
  pincode?: string;
  profession?: string;
  company_details?: string;
  gender?: string;
  state_id?: number;
  district_id?: number;
  taluk_id?: number;
  client_id?: number;
  added_on?: string;
  added_by?: number;
  approve_status?: number;
  contact_status?: number;
  remarks?: string;
  balance?: string;
  state_name?: string;
  district_name?: string;
  taluk_name?: string;
  membership_plan_id?: number;
  plan_name?: string;
  plan_type?: string;
  plan_duration_days?: number;
  plan_amount?: string;
  membership_join_date?: string;
  membership_expiry_date?: string;
  membership_status?: string;
}

export interface ContactDetails {
  contact_id: number;
  contact_name: string;
  contact_email: string;
  contact_mobile: string;
  // ... other fields
}

export interface ContactDesignationGroup {
  contact_id: number;
  contact_name: string;
  designations: Array<{
    designation_id: number;
    designation_name: string;
    group_count: number;
  }>;
  groups: Array<{
    group_id: number;
    group_name: string;
    member_count: number;
  }>;
}

interface ContactState {
  contacts: Contact[];
  clientContacts: Contact[];
  selectedContact: ContactDetails | null;
  contactProfile: Contact | null;
  contactDesignations: ContactDesignationGroup | null;
  loading: boolean;
  error: string | null;
  totalContacts: number;
}

const initialState: ContactState = {
  contacts: [],
  clientContacts: [],
  selectedContact: null,
  contactProfile: null,
  contactDesignations: null,
  loading: false,
  error: null,
  totalContacts: 0,
};

// Async Thunks
export const getAllContacts = createAsyncThunk(
  "contact/getAllContacts",
  async (_, { rejectWithValue }) => {
    const response = await getAllContactsApi();
    if (response.status) {
      return response.contacts || [];
    } else {
      return rejectWithValue(response.message || "Failed to fetch contacts");
    }
  }
);

export const getContactsByClient = createAsyncThunk(
  "contact/getContactsByClient",
  async (client_id: number, { rejectWithValue }) => {
    const response = await getContactsByClientApi(client_id);
    if (response.status) {
      return response.contacts || [];
    } else {
      return rejectWithValue(response.message || "Failed to fetch client contacts");
    }
  }
);

export const addContact = createAsyncThunk(
  "contact/addContact",
  async (contactData: FormData | Record<string, any>, { rejectWithValue }) => {
    const response = await addContactApi(contactData);
    if (response.status) {
      return response.contact || response.data;
    } else {
      return rejectWithValue(response.message || "Failed to add contact");
    }
  }
);

export const updateContact = createAsyncThunk(
  "contact/updateContact",
  async ({ id, contactData }: { id: number; contactData: FormData | Record<string, any> }, { rejectWithValue }) => {
    const response = await updateContactApi(id, contactData);
    if (response.status) {
      return response.contact || response.data;
    } else {
      return rejectWithValue(response.message || "Failed to update contact");
    }
  }
);

export const deleteContact = createAsyncThunk(
  "contact/deleteContact",
  async (id: number, { rejectWithValue }) => {
    const response = await deleteContactApi(id);
    if (response.status) {
      return { id };
    } else {
      return rejectWithValue(response.message || "Failed to delete contact");
    }
  }
);

export const addBulkContactsToGroup = createAsyncThunk(
  "contact/addBulkContactsToGroup",
  async (payload: { contact_ids: number[]; group_id: number }, { rejectWithValue }) => {
    const response = await addBulkContactsToGroupApi(payload);
    if (response.status) {
      return response.data;
    } else {
      return rejectWithValue(response.message || "Failed to add contacts to group");
    }
  }
);

export const getContactDetails = createAsyncThunk(
  "contact/getContactDetails",
  async (params: { contact_id?: number; contact_mobile?: string }, { rejectWithValue }) => {
    const response = await getContactDetailsApi(params);
    if (response.status) {
      return response.contact || response.data;
    } else {
      return rejectWithValue(response.message || "Failed to fetch contact details");
    }
  }
);

export const getContactProfile = createAsyncThunk(
  "contact/getContactProfile",
  async (params: { contact_id?: number; contact_mobile?: string }, { rejectWithValue }) => {
    const response = await getContactProfileApi(params);
    if (response.status) {
      return response.profile || response.data;
    } else {
      return rejectWithValue(response.message || "Failed to fetch contact profile");
    }
  }
);

export const getContactDesignationsAndGroups = createAsyncThunk(
  "contact/getContactDesignationsAndGroups",
  async (contact_id: number, { rejectWithValue }) => {
    const response = await getContactDesignationsAndGroupsApi(contact_id);
    if (response.status) {
      return response.data;
    } else {
      return rejectWithValue(response.message || "Failed to fetch designations and groups");
    }
  }
);

// Contact Slice
const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    clearSelectedContact: (state) => {
      state.selectedContact = null;
    },
    clearContactProfile: (state) => {
      state.contactProfile = null;
    },
    clearContactDesignations: (state) => {
      state.contactDesignations = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Contacts
      .addCase(getAllContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload;
        state.totalContacts = action.payload?.length || 0;
      })
      .addCase(getAllContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Get Contacts By Client
      .addCase(getContactsByClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContactsByClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clientContacts = action.payload;
        state.totalContacts = action.payload?.length || 0;
      })
      .addCase(getContactsByClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Add Contact
      .addCase(addContact.fulfilled, (state, action) => {
        if (action.payload) {
          state.clientContacts = [action.payload, ...state.clientContacts];
          state.contacts = [action.payload, ...state.contacts];
        }
      })
      
      // Update Contact
      .addCase(updateContact.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.clientContacts.findIndex(
            (c) => c.contact_id === action.payload.contact_id
          );
          if (index !== -1) {
            state.clientContacts[index] = action.payload;
          }
          const allIndex = state.contacts.findIndex(
            (c) => c.contact_id === action.payload.contact_id
          );
          if (allIndex !== -1) {
            state.contacts[allIndex] = action.payload;
          }
        }
      })
      
      // Delete Contact
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.clientContacts = state.clientContacts.filter(
          (c) => c.contact_id !== action.payload.id
        );
        state.contacts = state.contacts.filter(
          (c) => c.contact_id !== action.payload.id
        );
      })
      
      // Get Contact Details
      .addCase(getContactDetails.fulfilled, (state, action) => {
        state.selectedContact = action.payload;
      })
      
      // Get Contact Profile
      .addCase(getContactProfile.fulfilled, (state, action) => {
        state.contactProfile = action.payload;
      })
      
      // Get Contact Designations & Groups
      .addCase(getContactDesignationsAndGroups.fulfilled, (state, action) => {
        state.contactDesignations = action.payload;
      });
  },
});

export const {
  clearSelectedContact,
  clearContactProfile,
  clearContactDesignations,
  clearError,
} = contactSlice.actions;

export default contactSlice.reducer;